import { yupResolver } from '@hookform/resolvers/yup';
import Box from '@mui/material/Box';
import type { SxProps, Theme } from '@mui/material/styles';
import { DEFAULT_DATE_FORMAT } from '@openx/constants/date.ts';
import { format } from 'date-fns';
import { useCallback, useMemo, useRef, useState } from 'react';
import { FormProvider, useForm, useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import type { AnyObjectSchema } from 'yup';
import { PopoverTriggerButton } from '../Button/PopoverTriggerButton.tsx';
import { FiltersPopover } from '../FiltersPopover/FiltersPopover.tsx';
import type { DateRangeChipsProps } from './components/DateRangeChips.tsx';
import { DateRangePopoverContent } from './components/DateRangePopoverContent.tsx';
import { dateRangeFormFields } from './constants.ts';
import { isSameDate } from './utils/isSameDate.ts';

export type RHFDateRangePickerFieldProps = {
  'data-test'?: string;
  dateFormat?: string;
  startDateFormField: string;
  endDateFormField: string;
  minDate?: Date;
  maxDate?: Date;
  chipOptions?: DateRangeChipsProps['options'];
  endDateOptional?: boolean;
  startDateOptional?: boolean;
  triggerButtonSx?: SxProps<Theme>;
  validationSchema?: AnyObjectSchema;
  disabled?: boolean;
  loading?: boolean;
};

export const RHFDateRangePickerField = ({
  'data-test': dataTest = 'date-range-picker',
  dateFormat = DEFAULT_DATE_FORMAT,
  startDateFormField,
  endDateFormField,
  minDate,
  maxDate,
  chipOptions,
  endDateOptional = false,
  startDateOptional = false,
  triggerButtonSx,
  validationSchema,
  disabled = false,
  loading = false,
}: RHFDateRangePickerFieldProps) => {
  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);
  const isOpen = Boolean(anchorEl);

  const { t } = useTranslation();

  const { watch: externalFormWatch, setValue: externalFormSetValue } = useFormContext();

  const startDateExternalObservable = externalFormWatch(startDateFormField);
  const endDateExternalObservable = externalFormWatch(endDateFormField);

  const initialValuesRef = useRef({ startDate: startDateExternalObservable, endDate: endDateExternalObservable });

  const internalFormMethods = useForm({
    defaultValues: {
      [dateRangeFormFields.START_DATE]: startDateExternalObservable ?? null,
      [dateRangeFormFields.END_DATE]: endDateExternalObservable ?? null,
    },
    resolver: validationSchema && yupResolver(validationSchema),
  });

  const observableStartDateInternal = internalFormMethods.watch(dateRangeFormFields.START_DATE);
  const observableEndDateInternal = internalFormMethods.watch(dateRangeFormFields.END_DATE);

  const isClearable = useMemo(() => {
    //Form is clearable if internal form values are different then initial form values
    return (
      !isSameDate(observableStartDateInternal, initialValuesRef.current.startDate) ||
      !isSameDate(observableEndDateInternal, initialValuesRef.current.endDate)
    );
  }, [observableStartDateInternal, observableEndDateInternal]);

  const isApplyable = useMemo(() => {
    //Form is applyable if internal form values are different then external form values and internal form is valid
    return (
      (!isSameDate(observableStartDateInternal, startDateExternalObservable) ||
        !isSameDate(observableEndDateInternal, endDateExternalObservable)) &&
      internalFormMethods.formState.isValid
    );
  }, [
    observableStartDateInternal,
    observableEndDateInternal,
    startDateExternalObservable,
    endDateExternalObservable,
    internalFormMethods.formState.isValid,
  ]);

  const handleOpen = useCallback((event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  }, []);

  const hidePopover = useCallback(() => {
    setAnchorEl(null);
  }, []);

  const setInternalFormValues = useCallback(
    (startDate: Date, endDate: Date) => {
      internalFormMethods.setValue(dateRangeFormFields.START_DATE, startDate, {
        shouldDirty: true,
        shouldValidate: true,
      });
      internalFormMethods.setValue(dateRangeFormFields.END_DATE, endDate, {
        shouldDirty: true,
        shouldValidate: true,
      });
    },
    [internalFormMethods]
  );

  const setExternalFormValues = useCallback(
    (startDate: Date, endDate: Date) => {
      externalFormSetValue(startDateFormField, startDate, { shouldDirty: true, shouldValidate: true });
      externalFormSetValue(endDateFormField, endDate, { shouldDirty: true, shouldValidate: true });
    },
    [externalFormSetValue, startDateFormField, endDateFormField]
  );

  const handleClose = useCallback(() => {
    hidePopover();
    setInternalFormValues(startDateExternalObservable, endDateExternalObservable);
  }, [startDateExternalObservable, endDateExternalObservable, hidePopover, setInternalFormValues]);

  const handleApply = useCallback(() => {
    setExternalFormValues(
      internalFormMethods.getValues(dateRangeFormFields.START_DATE),
      internalFormMethods.getValues(dateRangeFormFields.END_DATE)
    );
    hidePopover();
  }, [internalFormMethods, setExternalFormValues, hidePopover]);

  const handleClear = useCallback(() => {
    setInternalFormValues(initialValuesRef.current.startDate, initialValuesRef.current.endDate);
    handleApply();
  }, [setInternalFormValues, handleApply]);

  const valueToRender = useMemo(() => {
    let startDateString = startDateExternalObservable
      ? format(startDateExternalObservable, dateFormat)
      : dateFormat.toUpperCase();
    if (!startDateExternalObservable && minDate) {
      startDateString = format(minDate, dateFormat);
    }

    let endDateString = endDateExternalObservable
      ? format(endDateExternalObservable, dateFormat)
      : dateFormat.toUpperCase();
    if (!endDateExternalObservable && maxDate) {
      endDateString = format(maxDate, dateFormat);
    }
    return `${startDateString} - ${endDateString}`;
  }, [startDateExternalObservable, endDateExternalObservable, dateFormat, maxDate, minDate]);

  const isEmpty = useMemo(() => {
    return !startDateExternalObservable && !endDateExternalObservable;
  }, [startDateExternalObservable, endDateExternalObservable]);

  return (
    <FormProvider {...internalFormMethods} data-test={dataTest}>
      <PopoverTriggerButton
        data-test={`${dataTest}-button`}
        onClick={handleOpen}
        isEmpty={isEmpty}
        isOpen={isOpen}
        sx={triggerButtonSx}
      >
        <Box component={'span'} sx={{ fontWeight: 'fontWeightBold' }}>
          <Box component={'span'} sx={{ textTransform: 'capitalize' }}>
            {t('Date')}
          </Box>
          : {valueToRender}
        </Box>
      </PopoverTriggerButton>

      <FiltersPopover
        data-test={`${dataTest}-popover`}
        open={isOpen}
        onClose={handleClose}
        onApply={handleApply}
        onClear={handleClear}
        title={t('Date')}
        anchorEl={anchorEl}
        isApplyable={isApplyable}
        isClearable={isClearable}
        slotProps={{
          paper: {
            sx: { width: '446px' },
          },
        }}
      >
        <DateRangePopoverContent
          data-test={`${dataTest}-popover-content`}
          minDate={minDate}
          maxDate={maxDate}
          chipOptions={chipOptions}
          dateFormat={dateFormat}
          endDateOptional={endDateOptional}
          startDateOptional={startDateOptional}
          disabled={disabled}
          loading={loading}
        />
      </FiltersPopover>
    </FormProvider>
  );
};
